import type { ClientError, ErrorOptions } from '../types';

export const shouldThrowError = (
  _err: ClientError,
  opts?: ErrorOptions,
): boolean => {
  // Do not throw errors in the browser because we don't want to trigger the
  // error boundary.
  if (typeof window !== 'undefined') {
    return false;
  }

  // Explicitly opt out of throwing errors for this error type
  if (opts && opts.neverThrowError) {
    return false;
  }

  return true;
};

// Print the error to the console
/* oxlint-disable no-console */
export const printError = (error: ClientError): void => {
  if (error) {
    console.group('Error reported:');
    console.error(error);
    console.groupEnd();
  }
};

// HTTP Status Codes we should ignore
export const STATUSES_TO_IGNORE = [401, 403, 404, 408, 500, 502, 503, 504];

// TODO @Luis improve error types.
export const shouldSendErrorToSentry = (
  error: Error | string | null,
): boolean => {
  if (!error) {
    return true;
  }

  // Check if we have a status code somewhere in this error

  const statusCode: number =
    // ServerParseError or SyntaxError parsing HTML
    // oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
    (error as any)?.statusCode ||
    // networkError
    // oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
    (error as any)?.networkError?.statusCode ||
    // WrappedError with networkError
    // oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
    (error as any)?.cause?.networkError?.statusCode;

  // NOTE: There is one more case we haven't handled yet, when
  // graphQLErrors contains the results of failing HTTP calls.
  // See https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-error#error-categorization

  if (STATUSES_TO_IGNORE.includes(statusCode)) {
    // Do not send this error to Sentry
    return false;
  }

  return true;
};

// A GraphQL endpoint that answers with HTML answered from infrastructure — a
// load balancer error page, a WAF challenge, a login redirect — never from the
// GraphQL layer, so nothing in the response is actionable on the FE.
//
// Apollo splits this across two error classes by status code: below 300 the
// JSON parse fails and it throws `ServerParseError`, at 300+
// `parseAndCheckHttpResponse` throws `ServerError` before attempting the parse
// at all. Matching the HTML body covers both.
//
// A present body decides on its own, and the content-type is consulted only
// when there is no body to read (links that don't populate `bodyText`). A false
// positive here suppresses an error silently, so a JSON body behind a
// misconfigured `text/html` header has to stay reportable.
export const isHtmlResponseError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const { bodyText, response } = error as {
    bodyText?: unknown;
    response?: { headers?: { get?: (name: string) => string | null } };
  };

  if (typeof bodyText === 'string' && bodyText.trim() !== '') {
    return bodyText.trimStart().startsWith('<');
  }

  const contentType = response?.headers?.get?.('content-type') ?? '';

  return contentType.toLowerCase().includes('text/html');
};

export const BROWSER_NOISE_ERRORS: (string | RegExp)[] = [
  // Benign error that means that ResizeObserver was not able to deliver all observations within a single animation frame.
  'ResizeObserver loop',
  // Browser extension conflicts (multiple wallet extensions trying to set window.ethereum)
  // These are not actionable - caused by third-party extensions (MetaMask, Nightly, etc)
  'Cannot set property ethereum',
  'Cannot redefine property: ethereum',
  /window\.ethereum/,
  // TronLink wallet extension attempts to inject tronlinkParams into window on every page.
  // Our proxy rejects it, causing a TypeError. Not actionable - caused by third-party extension.
  /tronlinkParams/,
  // Third-party wallet extensions (e.g. Coinbase Wallet) reject with this string when the user
  // dismisses a wallet prompt. Not actionable - user intentionally rejected the request.
  'user rejected the request',
  // MetaMask connection failures - caused by third-party extension issues, user denial, or extension not installed.
  // Not actionable - these are expected wallet interaction outcomes.
  'Failed to connect to MetaMask',
];

// Strings or regexps that match errors to ignore
export const SENTRY_IGNORE_ERRORS = [
  // Intermittent connectivity issue from the fetch API. Not actionable.
  // Retries are in place on the clients, respectively.
  'Failed to fetch',
  // Safari throws "Load failed" instead of the standard AbortError when a
  // fetch is cancelled by same-page navigation or component unmount. Not actionable.
  'Load failed',
  // Standard DOMException thrown when a fetch is aborted (e.g., by AbortController,
  // navigation, or component unmount). Not actionable.
  'AbortError',
  // Safari error
  'The network connection was lost',
  ...BROWSER_NOISE_ERRORS,
  'Invalid property descriptor',
  // SDK_READY_TIMED_OUT
  'SDK_READY_TIMED_OUT',
  // User attributes a deposit and tries to add the address as a trusted source which is already there
  'Already a trusted source',
  // User tries to add a Trusted Destination (Settings → Trusted Destinations) for an
  // address + asset type that already exists. BE returns a 400 (so it isn't caught by
  // the auto-suppressed 4xx>400 business-rule bucket in handleError) and the drawer UI
  // already surfaces the failure. Expected user-facing validation, not actionable. (JS-261)
  'already has a trusted destination',
  // User tries to create an API key with a public key that's already registered.
  // BE-side validation, surfaced in the form UI via <GraphQLError />.
  'Please enter an unused public key',
  // BO reviewer race conditions — when two reviewers act on the same KYC entity
  // or operation concurrently, the loser's approve/endorse is rejected because a
  // colleague got there first. Expected; surfaced in the UI (ONMON-4545 / JS-257).
  // Live messages: "This individual is not under review" (BACKOFFICE-JT),
  // "This operation has already been reviewed." (BACKOFFICE-JF).
  'not under review',
  'already been reviewed',
  // User submits a withdrawal/delegation from a wallet with none of the required
  // asset, typically an ERC-20 transfer with no native gas token and gas station
  // off. BE rejects with a 400 and the form already toasts it. Only the
  // "available 0" / "have 0" case is dropped: near-miss rejections (some balance,
  // not enough) have surfaced real rounding bugs before (VOC-492) and stay
  // reported. Covers the evaluator wording and the legacy holds wording. (JS-559)
  // Stopgap: remove once PRO2-1654 returns these as 422, which handleError
  // already drops.
  /Insufficient balance: required .* available 0 \w+/,
  /Insufficient balance available: have 0 \w+;/,
  // Password manager extensions and iOS Safari crash on login pages that don't
  // have this PWA meta tag, accessing .content on null. Not from our code.
  'apple-mobile-web-app-title',
];

// Only report errors originating from our own domains — filters out browser extension noise
export const SENTRY_ALLOW_URLS = [
  /anchoragelogin\.com(?:[:/]|$)/,
  /\.anchorage\.com(?:[:/]|$)/,
  /\.anchorage-development\.com(?:[:/]|$)/,
  /\.anchorage-staging\.com(?:[:/]|$)/,
  /\.anchorage-production\.com(?:[:/]|$)/,
  /\.cloudworkstations\.dev(?:[:/]|$)/,
];

export const isAlreadyTrustedSourceError = (error: string) =>
  error.startsWith('Already a trusted source');

// Matches stopRouting()'s thrown object (packages/hooks). Non-Error rejections get
// grouped by Sentry under a synthesized message, so match the raw rejection reason
// (hint.originalException in beforeSend) instead of SENTRY_IGNORE_ERRORS. (FRONTOFFICE-XF)
export const isNavigationBlockedRejection = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'NavigationBlocked';

// Chrome rejects a browser extension's `chrome.runtime.sendMessage` with this
// message when the other end is gone: an idle service worker, or a content
// script orphaned by an extension update. The rejection reaches the page's
// global handler with no stack frames, so `allowUrls` cannot filter it. Only
// the web apps drop it (see the Next.js `beforeSend`): the browser extension's
// own Sentry config shares SENTRY_IGNORE_ERRORS, and there a dead background
// worker is real signal, so the message must stay out of that list.
export const isExtensionMessagingRejection = (
  error: unknown,
  frames: readonly unknown[] | undefined,
): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { message?: unknown }).message ===
    'Could not establish connection. Receiving end does not exist.' &&
  (frames === undefined || frames.length === 0);

// In Apollo v4, network errors surface directly (no `.networkError` wrapper
// as in v3) — `error` here IS the network error. CEXP-19965.
export const isUnauthorizedError = (error: Error | undefined) => {
  if (!error) {
    return false;
  }

  return (
    'statusCode' in error &&
    (error as { statusCode?: number }).statusCode === 401
  );
};
